import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert
} from '@mui/material';
import { Save, Cancel, Warning } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { storyService } from '../services/api';
import ProfanityAwareTextField from '../components/ProfanityAwareTextField';
import { validateTextForProfanity } from '../utils/profanityFilter';
import { eventsService } from '../services/api';

const CreateStory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Environment variables
  const makeAllPrivate = true; // process.env.REACT_APP_MAKE_ALL_PRIVATE === 'true';
  const mustNeedEvent = true; // process.env.REACT_APP_MUST_NEED_EVENT === 'true';
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    status: 'draft',
    visibility: makeAllPrivate ? 'private' : 'private', // Set to private based on env var
    tags: [],
    event_id: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [profanityValidation, setProfanityValidation] = useState({
    title: { isValid: true, hasProfanity: false },
    content: { isValid: true, hasProfanity: false }
  });

  // Word count state
  const [wordCount, setWordCount] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Calculate word count
  const calculateWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Get word count validation for selected event
  const getWordCountValidation = () => {
    if (!selectedEvent || !selectedEvent.min_word_count && !selectedEvent.max_word_count) {
      return { valid: true, message: '' };
    }

    const count = wordCount;
    
    if (selectedEvent.min_word_count && count < selectedEvent.min_word_count) {
      return {
        valid: false,
        message: `Story must have at least ${selectedEvent.min_word_count} words. Current: ${count} words.`
      };
    }
    
    if (selectedEvent.max_word_count && count > selectedEvent.max_word_count) {
      return {
        valid: false,
        message: `Story must not exceed ${selectedEvent.max_word_count} words. Current: ${count} words.`
      };
    }
    
    return {
      valid: true,
      message: `Word count: ${count} words`
    };
  };

  // Load events for selection
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setEventsLoading(true);
        // Get only active events where user is participating (lightweight)
        const response = await eventsService.getActiveEvents();
        console.log('Active events dropdown API response:', response);
        
        // The API returns { data: { success: true, data: { events: [...] } } }
        const participatingEvents = response.data.data.events || [];
        
        console.log('Participating events for dropdown:', participatingEvents);
        setEvents(participatingEvents);
      } catch (err) {
        console.error('Error loading active events:', err);
        // Don't show error for events loading failure, it's optional
      } finally {
        setEventsLoading(false);
      }
    };

    loadEvents();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent changing visibility if makeAllPrivate is true
    if (name === 'visibility' && makeAllPrivate) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update word count when content changes
    if (name === 'content') {
      setWordCount(calculateWordCount(value));
    }

    // Update selected event when event_id changes
    if (name === 'event_id') {
      const event = events.find(e => e.event_id === value);
      setSelectedEvent(event || null);
    }
  };

  const handleProfanityValidation = (field) => (validation) => {
    setProfanityValidation(prev => ({
      ...prev,
      [field]: validation
    }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      
      // Check tag for profanity before adding
      const tagValidation = validateTextForProfanity(tagInput.trim());
      if (tagValidation.hasProfanity) {
        setError(`Tag contains inappropriate language: ${tagValidation.profaneWords.join(', ')}`);
        return;
      }
      
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      setTagInput('');
      setError(null);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required');
      return;
    }

    // Check if event is required
    if (mustNeedEvent && !formData.event_id) {
      setError('Event selection is required');
      return;
    }

    // Check word count validation for selected event
    const wordCountValidation = getWordCountValidation();
    if (!wordCountValidation.valid) {
      setError(wordCountValidation.message);
      return;
    }

    // Check for profanity before submitting
    const titleValidation = validateTextForProfanity(formData.title);
    const contentValidation = validateTextForProfanity(formData.content);
    
    if (titleValidation.hasProfanity || contentValidation.hasProfanity) {
      const profaneWords = [...titleValidation.profaneWords, ...contentValidation.profaneWords];
      setError(`Please remove inappropriate language before publishing: ${profaneWords.join(', ')}`);
      return;
    }

    // Check tags for profanity
    for (const tag of formData.tags) {
      const tagValidation = validateTextForProfanity(tag);
      if (tagValidation.hasProfanity) {
        setError(`Tag "${tag}" contains inappropriate language: ${tagValidation.profaneWords.join(', ')}`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      const response = await storyService.createStory(formData);
      
      // Debug: Log the full response to see what we're getting
      console.log('Story creation response:', response);
      console.log('Response data:', response.data);
      
      // Handle nested response structure: response.data.data.story_id
      const storyId = response.data?.data?.story_id || response.data?.story_id;
      console.log('Story ID:', storyId);
      
      if (storyId) {
        navigate(`/story/${storyId}`);
      } else {
        console.error('No story_id in response:', response);
        setError('Story was created but navigation failed. Please check your stories list.');
        // Fallback navigation
        setTimeout(() => navigate('/stories'), 2000);
      }
    } catch (err) {
      setError('Failed to create story');
      console.error('Error creating story:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/stories');
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Please log in to create a story.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Story
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <ProfanityAwareTextField
            fullWidth
            label="Story Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            onValidationChange={handleProfanityValidation('title')}
            margin="normal"
            required
            variant="outlined"
            placeholder="Enter your story title..."
            showProfanityWarning={true}
            showCleanSuggestion={true}
          />

          <ProfanityAwareTextField
            fullWidth
            label="Story Content"
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            onValidationChange={handleProfanityValidation('content')}
            margin="normal"
            required
            multiline
            rows={12}
            variant="outlined"
            placeholder="Write your story here..."
            showProfanityWarning={true}
            showCleanSuggestion={true}
          />

          {/* Word Count Display */}
          <Box sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Word count: {wordCount}
            </Typography>
            {selectedEvent && (selectedEvent.min_word_count || selectedEvent.max_word_count) && (
              <Typography 
                variant="body2" 
                color={getWordCountValidation().valid ? 'success.main' : 'error.main'}
              >
                {selectedEvent.min_word_count && selectedEvent.max_word_count 
                  ? `Required: ${selectedEvent.min_word_count} - ${selectedEvent.max_word_count} words`
                  : selectedEvent.min_word_count 
                    ? `Minimum: ${selectedEvent.min_word_count} words`
                    : `Maximum: ${selectedEvent.max_word_count} words`
                }
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2, mb: 2 }}>
            <ProfanityAwareTextField
              fullWidth
              label="Add Tags (press Enter to add)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleAddTag}
              variant="outlined"
              helperText="Press Enter to add a tag"
              showProfanityWarning={true}
              showCleanSuggestion={false}
            />
            <Box sx={{ mt: 1 }}>
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              label="Status"
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Visibility</InputLabel>
            <Select
              name="visibility"
              value={formData.visibility}
              onChange={handleInputChange}
              label="Visibility"
              disabled={makeAllPrivate}
            >
              <MenuItem value="private">Private</MenuItem>
              {!makeAllPrivate && <MenuItem value="public">Public</MenuItem>}
            </Select>
            {makeAllPrivate && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                All stories are set to private by default
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>{mustNeedEvent ? 'Link to Event (Required)' : 'Link to Event (Optional)'}</InputLabel>
            <Select
              name="event_id"
              value={formData.event_id}
              onChange={handleInputChange}
              label={mustNeedEvent ? 'Link to Event (Required)' : 'Link to Event (Optional)'}
              disabled={eventsLoading}
              required={mustNeedEvent}
            >
              {!mustNeedEvent && (
                <MenuItem value="">
                  <em>No Event</em>
                </MenuItem>
              )}
              {events.map((event) => (
                <MenuItem key={event.event_id} value={event.event_id}>
                  {event.title} ({new Date(event.start_date).toLocaleDateString()})
                </MenuItem>
              ))}
            </Select>
            {eventsLoading && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Loading events...
              </Typography>
            )}
            {mustNeedEvent && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Event selection is required for all stories
              </Typography>
            )}
          </FormControl>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? 'Creating...' : 'Create Story'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateStory;
